import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  UserCircle2,
  LogOut,
  Briefcase,
  Plus,
  ShieldCheck,
  X,
  Check,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useWorkspace } from "@/lib/workspace-context";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";

/* The ONE canonical Intuiterview journey. The sidebar is the only overall
   journey navigation; pages never duplicate the 01-05 progression. */
export const JOURNEY_NAV = [
  { to: "/app/resume", num: "01", title: "Stand Out", label: "STAND OUT", testid: "nav-resume" },
  { to: "/app/prep", num: "02", title: "Predict & Prepare", label: "PREDICT & PREPARE", testid: "nav-prep" },
  { to: "/app/practice", num: "03", title: "Practice", label: "PRACTICE", testid: "nav-practice", extraPaths: ["/app/video-mock", "/app/mock"] },
  { to: "/app/live", num: "04", title: "Official AI Interview", label: "OFFICIAL AI INTERVIEW", testid: "nav-live", premium: true },
  { to: "/app/briefs", num: "05", title: "Interview Brief", label: "INTERVIEW BRIEFS", testid: "nav-briefs" },
];

/* Refined emerald "I AM HERE" state, shared by every sidebar destination. */
const ACTIVE_CLS = "bg-emerald-500/[0.09] text-emerald-300 border border-emerald-500/30 shadow-sm";
const INACTIVE_CLS = "text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900/60 border border-transparent";
const ITEM_BASE = "flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-colors duration-150";

export function pathMatches(pathname, to, extraPaths = []) {
  return [to, ...extraPaths].some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export default function Sidebar({ mobileOpen = false, onCloseMobile }) {
  const { user, logout } = useAuth();
  const { workspaces, canCreateMore, activeId, loading: wsLoading } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  // Per-stage completion flags (light endpoint, workspace-scoped). Refetched
  // on navigation so checkmarks appear as soon as a stage is completed.
  const [doneStages, setDoneStages] = useState({});
  useEffect(() => {
    if (wsLoading || !user) return;
    let alive = true;
    api.get("/dashboard/journey")
      .then(({ data }) => {
        if (!alive) return;
        const m = {};
        (data?.stages || []).forEach((s) => { m[s.num] = !!s.done; });
        setDoneStages(m);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [wsLoading, activeId, user?.user_id, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only show the "My Interviews" section once the user actually has multiple
  // interviews to switch between. Premium users at exactly 1 interview still
  // get a subtle "Prepare for another" CTA so they can add a second one.
  const showInterviewsNav = workspaces.length >= 2;
  const showAddAnotherCta = workspaces.length === 1 && canCreateMore;

  // My Account should light up for /app/account/* and legacy /app/profile
  const accountActive =
    location.pathname.startsWith("/app/account") ||
    location.pathname.startsWith("/app/profile") ||
    location.pathname.startsWith("/app/billing");

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <aside
      data-testid="app-sidebar"
      className={[
        "fixed left-0 top-0 h-screen w-64 border-r border-zinc-800/60 bg-[#0A0A0B]/95 backdrop-blur-md flex flex-col z-50 md:z-30",
        "transform transition-transform duration-200 ease-out",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        "md:translate-x-0",
      ].join(" ")}
    >
      <div className="px-6 pt-7 pb-5 flex items-center justify-between">
        <BrandLogo
          variant="lockup"
          iconSize={34}
          to="/app/dashboard"
          testid="sidebar-brand"
        />
        <button
          data-testid="sidebar-close-mobile-btn"
          aria-label="Close menu"
          onClick={onCloseMobile}
          className="md:hidden h-8 w-8 -mr-2 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* WorkspaceSwitcher auto-hides when the user has ≤1 interview. */}
      <div className="px-3 pb-3">
        <WorkspaceSwitcher />
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto [scrollbar-width:thin]">
        <NavLink
          to="/app/dashboard"
          end
          data-testid="nav-dashboard"
          className={({ isActive }) => [ITEM_BASE, isActive ? ACTIVE_CLS : INACTIVE_CLS].join(" ")}
        >
          <LayoutDashboard className="h-4 w-4" strokeWidth={2} />
          <span className="font-medium tracking-tight flex-1">Dashboard</span>
        </NavLink>

        <div
          data-testid="sidebar-journey-label"
          className="px-3.5 pt-4 pb-1.5 text-[9px] uppercase tracking-[0.28em] text-zinc-500 font-semibold"
        >
          Your Journey
        </div>
        {JOURNEY_NAV.map(({ to, num, label, testid, premium, extraPaths }) => {
          const active = pathMatches(location.pathname, to, extraPaths);
          const done = !!doneStages[num];
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={[ITEM_BASE, active ? ACTIVE_CLS : INACTIVE_CLS].join(" ")}
            >
              <span
                className={`text-[10px] font-bold tabular-nums w-4 text-center flex-shrink-0 ${
                  active ? "text-emerald-300" : "text-zinc-600"
                }`}
              >
                {num}
              </span>
              <span className="text-[11px] uppercase tracking-[0.12em] font-semibold flex-1">{label}</span>
              {done && (
                <Check
                  data-testid={`journey-done-${num}`}
                  className="h-3 w-3 text-emerald-400/70 flex-shrink-0"
                  strokeWidth={3}
                  aria-label="Completed"
                />
              )}
              {premium && (
                <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-1.5 py-0.5">
                  Pro
                </span>
              )}
            </NavLink>
          );
        })}

        {showInterviewsNav && (
          <NavLink
            to="/app/workspaces"
            data-testid="nav-interviews"
            className={({ isActive }) => [ITEM_BASE, "mt-3", isActive ? ACTIVE_CLS : INACTIVE_CLS].join(" ")}
          >
            <Briefcase className="h-4 w-4" strokeWidth={2} />
            <span className="font-medium tracking-tight flex-1">My Interviews</span>
            <span className="text-[10px] tabular-nums text-zinc-500">{workspaces.length}</span>
          </NavLink>
        )}

        <NavLink
          to="/app/account"
          data-testid="nav-account"
          className={() => [ITEM_BASE, showInterviewsNav ? "" : "mt-3", accountActive ? ACTIVE_CLS : INACTIVE_CLS].join(" ")}
        >
          <UserCircle2 className="h-4 w-4" strokeWidth={2} />
          <span className="font-medium tracking-tight flex-1">My Account</span>
        </NavLink>

        {/* Owner Analytics, admin-only. Frontend hides the link when the
            user isn't admin; the backend enforces the same rule on every
            /api/admin/analytics/* endpoint so the guard cannot be bypassed. */}
        {user?.role === "admin" && (
          <NavLink
            to="/app/admin/analytics"
            data-testid="nav-admin-analytics"
            className={({ isActive }) => [ITEM_BASE, isActive ? ACTIVE_CLS : INACTIVE_CLS].join(" ")}
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2} />
            <span className="font-medium tracking-tight flex-1">Owner Analytics</span>
            <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-1.5 py-0.5">
              Admin
            </span>
          </NavLink>
        )}
      </nav>

      {showAddAnotherCta && (
        <div className="mx-3 mb-2">
          <Link
            to="/app/workspaces"
            data-testid="sidebar-add-another-interview"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-zinc-400 hover:text-amber-300 hover:bg-amber-500/[0.06] border border-dashed border-zinc-800 hover:border-amber-500/25 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Prepare for another interview
          </Link>
        </div>
      )}

      <div className="mx-3 mb-4 mt-2 p-3 rounded-xl border border-zinc-800/60 bg-zinc-900/50">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-xs font-semibold text-zinc-100 border border-zinc-700">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="h-full w-full rounded-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-zinc-100 truncate" data-testid="sidebar-user-name">
              {user?.name || "You"}
            </div>
            <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="sidebar-logout-btn"
            onClick={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
            title="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
